import { MouseEventHandler, ReactElement, ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import { Box, Typography } from "@mui/material";
import {
  DataGridProProps,
  GridColDef,
  GridRenderCellParams,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  useGridApiContext,
} from "@mui/x-data-grid-pro";
import { DateTime } from "luxon";
import { useDispatch, useSelector } from "react-redux";
import { IoChevronDown, IoChevronForward, IoEllipsisHorizontal, IoEyeOffOutline } from "react-icons/io5";

import { CabAvatar, CabButton, CabCollapseMenu, CabExecPicker, CabIcon, CabTabs } from "@CabComponents/index";
import CabDataGrid from "@CabComponents/CabDataGrid";
import { CabInput } from "@CabComponents/CabInput";
import { CabTooltip } from "@CabComponents/CabTooltip";
import CabinetPage from "../../components/Common/CabinetPage";
import colors from "../../colors";
import { Leader, RootState, ThunkDispatchType } from "../../store";
import { analytics, RecurringMeetingSeries } from "../../store/cabinetApi/analytics";
import { sendMessage } from "../../store/globalMessages/actions";
import { getLeaderIconSrc, getLeadersForScheduling } from "../../utils/leaderUtils";

const formatDate = (value: string) => DateTime.fromISO(value).toFormat("M/dd/yyyy");
const formatTime = (value: string) => DateTime.fromISO(value).toFormat("h:mm a");
const formatFrequency = (value?: string | null) => value ? value.charAt(0).toUpperCase() + value.slice(1) : "";

const formatDuration = (minutes: number) => {
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  }
  const hours = minutes / 60;
  return Number.isInteger(hours)
    ? `${hours} hour${hours !== 1 ? "s" : ""}`
    : `${hours.toFixed(2)} hours`;
};

const RecurringMeetingsToolbar = () => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", width: "100%" }}>
    <GridToolbarFilterButton />
    <GridToolbarColumnsButton />
  </Box>
);

const RecurringSeriesGroupingCell = (props: GridRenderCellParams<RecurringMeetingSeries>) => {
  const { id, field, row, rowNode } = props;
  const apiRef = useGridApiContext();
  const childCount = row.grouping_child_ids.length;
  const isChild = rowNode.depth > 0;

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (rowNode.type !== "group") {
      return;
    }

    apiRef.current.setRowChildrenExpansion(id, !rowNode.childrenExpanded);
    apiRef.current.setCellFocus(id, field);
    event.stopPropagation();
  };

  if (isChild) return null;

  return (
    <CabButton
      onClick={handleClick}
      disabled={childCount === 0}
      buttonType="text"
      size="small"
      icon={<CabIcon Icon={rowNode.type === "group" && rowNode.childrenExpanded ? IoChevronDown : IoChevronForward} />}
      sx={{
        paddingLeft: 0,
        minWidth: 36,
        "& .MuiButton-startIcon": { margin: 0 },
      }}
    >
    </CabButton>
  );
};

const renderAttendees = ({ row }: GridRenderCellParams<RecurringMeetingSeries, ReactNode>): ReactNode => {
  if (!row.attendees.length) return "";

  return (
    <CabTooltip
      title={row.attendees.join("\n")}
      wrapWithSpan
      spanSx={{
        display: "block",
        height: "100%",
        width: "100%",
      }}
    >
      <Box sx={{ position: "relative", height: "100%", width: "100%" }}>
        <Typography
          sx={{
            fontSize: "inherit",
            fontWeight: "inherit",
            lineHeight: "inherit",
            position: "absolute",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          {row.attendees[0]}
        </Typography>
        {row.attendees.length > 1 && (
          <Typography
            sx={{
              color: colors.black600,
              fontSize: 12,
              lineHeight: 1.1,
              position: "absolute",
              top: "calc(50% + 10px)",
            }}
          >
            +{row.attendees.length - 1} more
          </Typography>
        )}
      </Box>
    </CabTooltip>
  );
};

const getColumns = (
  onHideRecurringSeries: (row: RecurringMeetingSeries) => void,
): GridColDef<RecurringMeetingSeries>[] => [
  {
    field: "meeting_name",
    headerName: "Meeting Name",
    flex: 1.3,
    minWidth: 220,
  },
  {
    field: "frequency",
    headerName: "Frequency",
    width: 170,
    valueFormatter: formatFrequency,
  },
  {
    field: "day_of_week",
    headerName: "Day of Week",
    width: 140,
  },
  {
    field: "time",
    headerName: "Time",
    width: 120,
    valueFormatter: (value?: string) => value ? formatTime(value) : "",
  },
  {
    field: "duration_minutes",
    headerName: "Duration",
    width: 130,
    valueFormatter: (value?: number) => value ? formatDuration(value) : "",
  },
  {
    field: "attendees",
    headerName: "Attendees",
    flex: 1,
    minWidth: 220,
    sortable: false,
    renderCell: renderAttendees,
    valueFormatter: (value?: string[]) => value?.join(", ") || "",
  },
  {
    field: "meeting_owner",
    headerName: "Calendar",
    flex: 1,
    minWidth: 180,
  },
  {
    field: "meeting_organizer",
    headerName: "Meeting Organizer",
    flex: 1,
    minWidth: 200,
  },
  {
    field: "series_start_date",
    headerName: "Series Start Date",
    width: 170,
    valueFormatter: (value?: string) => value ? formatDate(value) : "",
  },
  {
    field: "series_end_date",
    headerName: "Series End Date",
    width: 160,
    valueFormatter: (value?: string | null) => value ? formatDate(value) : "",
  },
  {
    field: "Options",
    disableColumnMenu: true,
    resizable: false,
    hideable: false,
    headerName: "",
    display: "flex",
    width: 50,
    filterable: false,
    sortable: false,
    renderCell: ({ row }) => {
      if (row.grouping_parent_id) return null;

      return (
        <CabCollapseMenu
          buttons={
            <CabButton
              buttonType="text"
              onClick={() => onHideRecurringSeries(row)}
              icon={<CabIcon Icon={IoEyeOffOutline} color={colors.black900}/>}
              sx={{
                justifyContent: "start",
                color: colors.black900,
                paddingBottom: 1,
                paddingTop: 1,
                paddingLeft: 2,
                paddingRight: 2,
                width: "100%",
              }}
            >
              Hide
            </CabButton>
          }
          target={<CabIcon
            Icon={IoEllipsisHorizontal}
            alt="Options"
            sx={{ marginRight: 2, color: colors.black800, fontSize: 20 }}
          />}
          popOverTitle=""
          popOverAnchorOrigin={{
            horizontal: -50,
            vertical: 30,
          }}
        />
      );
    },
  },
];

const initialState: DataGridProProps["initialState"] = {
  pinnedColumns: {
    left: ["__tree_data_group__", "meeting_name"],
    right: ["Options"],
  },
};

const groupingColDef: DataGridProProps["groupingColDef"] = {
  headerName: "",
  renderCell: (params) => <RecurringSeriesGroupingCell {...params} />,
  width: 60,
  hideable: false,
  display: "flex",
};

const getTreeDataPath: DataGridProProps["getTreeDataPath"] = (row) => row.hierarchy;

const getRowClassName: DataGridProProps["getRowClassName"] = (params) => (
  params.row.grouping_parent_id ? "recurring-meeting-child" : ""
);

const RecurringMeetings = (): ReactElement => {
  const dispatch = useDispatch<ThunkDispatchType>();
  const leaders = useSelector((state: RootState) => state.leaders.leaders);
  const myLeaders = useMemo(() => getLeadersForScheduling(leaders), [leaders]);
  const [selectedLeader, setSelectedLeader] = useState<Leader | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [hideRecurringSeries] = analytics.useAppAnalyticsEventsHideRecurringSeriesMutation();

  useEffect(() => {
    if (!selectedLeader && myLeaders.length) {
      setSelectedLeader(myLeaders[0]);
    }
  }, [myLeaders, selectedLeader]);

  const execOptions = useMemo(() => myLeaders.map(leader => ({
    value: leader.id,
    label: `${leader.first_name} ${leader.last_name}`,
    icon: (
      <CabAvatar
        src={getLeaderIconSrc(leader)}
        color={leader.color}
        name={`${leader.first_name} ${leader.last_name}`}
        size="small"
      />
    ),
    disabled: leader.is_shared && !Object.values(leader.permissions.scheduling).some((perm: boolean) => perm),
  })), [myLeaders]);

  const activeQueryArgs = selectedLeader
    ? {
      active: true,
      leader: selectedLeader.id,
      search: searchInput || undefined,
    }
    : skipToken;
  const inactiveQueryArgs = selectedLeader
    ? {
      active: false,
      leader: selectedLeader.id,
      search: searchInput || undefined,
    }
    : skipToken;

  const { data: activeRecurringMeetings = [], isFetching: activeFetching } =
    analytics.useAppAnalyticsEventsRecurringSeriesListQuery(activeQueryArgs);
  const { data: inactiveRecurringMeetings = [], isFetching: inactiveFetching } =
    analytics.useAppAnalyticsEventsRecurringSeriesListQuery(inactiveQueryArgs);

  const handleHideRecurringSeries = useCallback(async (row: RecurringMeetingSeries) => {
    try {
      await hideRecurringSeries({
        leader: row.leader,
        normalized_meeting_name: row.normalized_meeting_name,
        sample_recurring_root_event_idr: row.recurring_root_event_idr,
      }).unwrap();
    } catch {
      dispatch(sendMessage({
        timeout: 5000,
        message: "We couldn't hide that recurring meeting group. Please try again.",
        autoDismiss: true,
        header: "",
        position: {
          vertical: "top",
          horizontal: "center",
        },
        active: true,
        severity: "error",
      }));
    }
  }, [dispatch, hideRecurringSeries]);

  const columns = useMemo(() => getColumns(handleHideRecurringSeries), [handleHideRecurringSeries]);

  const renderGrid = (
    rows: RecurringMeetingSeries[],
    loading: boolean,
    noRowsLabel: string,
  ) => {
    const treeRows = rows.map(row => ({
      ...row,
      hierarchy: row.grouping_parent_id ? [row.grouping_parent_id, row.id] : [row.id],
    }));

    return (
      <Box sx={{ position: "relative", height: "100%", minHeight: 0, paddingTop: 2 }}>
        <CabDataGrid
          rows={treeRows}
          columns={columns}
          rowHeight={60}
          loading={loading}
          disableRowSelectionOnClick
          initialState={initialState}
          treeData
          getTreeDataPath={getTreeDataPath}
          groupingColDef={groupingColDef}
          getRowClassName={getRowClassName}
          slots={{
            toolbar: RecurringMeetingsToolbar,
          }}
          localeText={{
            noRowsLabel,
          }}
          sx={{
            position: "absolute",
            top: 16,
            bottom: 0,
            left: 0,
            right: 0,
            "& .recurring-meeting-child": {
              backgroundColor: colors.black100,
            },
          }}
        />
      </Box>
    );
  };

  const noLeaderLabel = "Select a leader to view recurring meetings";
  const activeNoRowsLabel = selectedLeader ? "No active recurring meetings found" : noLeaderLabel;
  const inactiveNoRowsLabel = selectedLeader ? "No inactive recurring meetings found" : noLeaderLabel;

  const handleLeaderSelect = (leaderId: number) => {
    setSelectedLeader(myLeaders.find(leader => leader.id === leaderId) || null);
  };

  return (
    <CabinetPage
      pageName="Recurring Meetings"
      headerContent={
        <Box sx={{ width: "100%", paddingLeft: 2 }}>
          <Typography variant="h1">Recurring Meetings</Typography>
        </Box>
      }
    >
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: 5,
        paddingTop: 4,
        gap: 2,
      }}>
        <Typography sx={{ color: colors.black600 }}>
          Review standing calendar meetings for the selected team member.
        </Typography>
        <Box sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          alignItems: { xs: "stretch", sm: "end" },
        }}>
          <Box>
            <Typography>Show meetings for</Typography>
            <CabExecPicker<number>
              value={selectedLeader?.id || null}
              options={execOptions}
              onChange={handleLeaderSelect}
              placeholder="Select leader"
              sx={{ maxWidth: "100%", "& .select-value-label": { fontWeight: "bold", fontSize: 20 } }}
            />
          </Box>
          <CabInput
            placeholder="Search..."
            fullWidth
            size="small"
            sx={{ minWidth: 220, maxWidth: 360 }}
            value={searchInput}
            onChange={(e) => setSearchInput(e.currentTarget.value)}
          />
        </Box>
        <CabTabs
          tabs={[{ label: "Active" }, { label: "Inactive" }]}
          align="left"
          hideDivider
          sx={{ flex: 1, minHeight: 0 }}
          tabPanelSx={{ marginTop: 0, height: "100%", position: "relative" }}
        >
          {renderGrid(activeRecurringMeetings, activeFetching, activeNoRowsLabel)}
          {renderGrid(inactiveRecurringMeetings, inactiveFetching, inactiveNoRowsLabel)}
        </CabTabs>
      </Box>
    </CabinetPage>
  );
};

export default RecurringMeetings;
